import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from '@innovation-os-ng/core/logger';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { Observable, ReplaySubject, take, tap } from 'rxjs';
import { EntityConfigRelationType } from '../model/relationType';

@Injectable({
  providedIn: 'root'
})
export class EntityConfigRelationTypeService {
  private apiUrl: string = 'elements-config/relation-types';

  private relationTypesSubject: ReplaySubject<Array<EntityConfigRelationType>> = new ReplaySubject<Array<EntityConfigRelationType>>(1);
  private relationTypesCache: Array<EntityConfigRelationType>;

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly logger: LogService
  ) {}

  /**
   * Loads relation types from the server and updates the cache and subject.
   *
   * @returns An observable as Array<EntityConfigRelationType> that emits the relation types.
   */
  private loadRelationTypes(): Observable<Array<EntityConfigRelationType>> {
    const url: string = this.environmentService.getRestUrl(this.apiUrl);
    return this.http.get<Array<EntityConfigRelationType>>(url).pipe(
      take(1),
      tap({
        next: (res) => {
          this.relationTypesCache = res; // Update the cache
          this.relationTypesSubject.next(res.map((relation) => ({ ...relation, inverseFieldUri: relation.inverseUri })));
        },
        error: (err) => {
          this.logger.warn('RelationTypeService: Failed to load relation types', { error: err });
        }
      })
    );
  }

  /**
   * Retrieves the relation types. If the cache is empty, it loads the data from the server.
   *
   * @returns An observable as Array<EntityConfigRelationType> that emits the relation types.
   */
  public getRelationTypes(): Observable<Array<EntityConfigRelationType>> {
    if (!this.relationTypesCache) {
      // If the cache is empty, load data from the server and subscribe to the observable
      this.loadRelationTypes().subscribe();
    }
    return this.relationTypesSubject.asObservable();
  }
}
